import { useLayoutEffect, useRef, type MouseEvent, type ReactNode } from "react";
import { Box } from "../../components/ui/box";
import { Text } from "../../components/ui/text";
import { useElementSize } from "../../hooks/use-element-size";
import type { Omittable } from "../../lib/props";
import { useChartColors, type ChartColorToken } from "./colors";
import { ChartTooltipCard, useChartHover } from "./tooltip";

/** One segment of the whole. */
export type ProportionBarSegment = Omittable<{
  id: string;
  label: string;
  value: number;
}>;

export type ProportionBarProps = Omittable<{
  segments: readonly ProportionBarSegment[];
  /** Index-aligned segment colors (`useCategoricalColors(...).tokens`) so
   * color follows the entity, never its sort position. */
  colorTokens: readonly ChartColorToken[];
  /** Bar thickness in px. Defaults to 48. */
  thickness?: number;
  /** Print each segment's label inside its colored block — only where the
   * WHOLE label fits. Narrow segments stay quiet (a partial name reads as a
   * different name); the hover card still names them. */
  showLabels?: boolean;
  /** Makes each segment a real button. */
  onSegmentClick?: (segment: ProportionBarSegment) => void;
  /** Hover card content for the hovered segment; omit for no hover card. */
  renderTooltip?: (segment: ProportionBarSegment) => ReactNode;
  /** One segment id to hold at full ink while every other segment recedes —
   *  a legend or a sibling control pointing into the bar. */
  focusedId?: string | null;
  /** The share of the track the whole bar spans, 0–1. Rows of bars on one
   *  scale (each row's total against the largest) compare by length. */
  span?: number;
  ariaLabel?: string;
}>;

/** Focus fades; a reduced-motion viewer gets the new state at once. */
const SEGMENT_MOTION = "transition-opacity duration-200 motion-reduce:transition-none";

/** A receded segment's opacity while another is focused. */
const UNFOCUSED_OPACITY = 0.2;

/**
 * The finished part-to-whole bar: one row of fully-rounded segments sized by
 * value share, separated by hairline gaps. Pure DOM — no chart engine — so it
 * stays crisp at any size. Colors are semantic tokens painted as live `var()`
 * strings.
 */
export function ProportionBar({
  segments,
  colorTokens,
  thickness = 48,
  showLabels,
  onSegmentClick,
  renderTooltip,
  focusedId,
  span = 1,
  ariaLabel,
}: ProportionBarProps) {
  const c = useChartColors();
  // The measured ref carries `.current` too, so the tooltip's bounds read and
  // the resize-driven re-render share one attachment.
  const { ref: containerRef } = useElementSize<HTMLDivElement>();
  const labelRefs = useRef(new Map<string, HTMLSpanElement>());
  const { hover, show, hide } = useChartHover<ProportionBarSegment>();
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const radius = Math.min(8, thickness / 2);

  // Whole-label-or-nothing, decided against the real layout: a flex share is
  // not knowable from the data, so each label is measured where it landed.
  // Labels mount `invisible` and this flips visibility inline before paint —
  // a visibility flip cannot change layout, so measuring here cannot loop. No
  // dependency array on purpose: the container resize re-renders through
  // `useElementSize`, and any data change re-renders on its own.
  useLayoutEffect(() => {
    if (!showLabels) return;
    for (const label of labelRefs.current.values()) {
      label.style.visibility =
        label.scrollWidth <= label.clientWidth ? "visible" : "hidden";
    }
  });

  const track = renderTooltip
    ? (segment: ProportionBarSegment) => (event: MouseEvent<HTMLElement>) => {
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds) return;
        show(segment, {
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
        });
      }
    : undefined;

  return (
    <Box
      ref={containerRef}
      position="relative"
      width="100%"
      role={ariaLabel === undefined ? undefined : "group"}
      aria-label={ariaLabel}
    >
      <Box
        direction="row"
        align="center"
        width={`${(Number.isFinite(span) ? Math.max(0, Math.min(1, span)) : 1) * 100}%`}
        className="gap-[3px]"
      >
        {segments.map((segment, i) => {
          const style = {
            flexGrow: total > 0 ? segment.value / total : 0,
            flexBasis: 0,
            height: thickness,
            borderRadius: radius,
            backgroundColor: c.paint(colorTokens[i]),
            opacity: focusedId && focusedId !== segment.id ? UNFOCUSED_OPACITY : 1,
            // A wide label must never stretch its segment past its share —
            // flex items default to their content's minimum width.
            ...(showLabels ? { minWidth: 0 } : null),
          };
          const hoverHandlers = track
            ? { onMouseMove: track(segment), onMouseLeave: hide }
            : null;
          // The label paints `var(--c-foreground)` off the `data-color`
          // cascade, so the browser pairs readable ink to the segment fill and
          // re-pairs on a theme flip — nothing is resolved in JS. It rests
          // `invisible`; the layout effect above reveals only the ones that fit.
          // The breathing inset is margin ON THE LABEL, never padding on the
          // segment: `border-box` floors a padded segment at its padding ring
          // regardless of `min-width: 0`, which hands every sliver a flat 16px
          // head start and quietly bends the bar's value shares.
          const labelLayout = showLabels
            ? ({
                direction: "row",
                align: "center",
                justify: "center",
                "data-color": colorTokens[i],
              } as const)
            : null;
          const label = showLabels ? (
            <Text
              ref={(el: HTMLSpanElement | null) => {
                if (el) labelRefs.current.set(segment.id, el);
                else labelRefs.current.delete(segment.id);
              }}
              size="xs"
              weight="semibold"
              className="invisible mx-2 min-w-0 overflow-hidden whitespace-nowrap text-c-foreground"
            >
              {segment.label}
            </Text>
          ) : null;
          return onSegmentClick ? (
            <Box
              key={segment.id}
              as="button"
              type="button"
              aria-label={segment.label}
              style={{ ...style, cursor: "pointer" }}
              className={SEGMENT_MOTION}
              onClick={() => onSegmentClick(segment)}
              {...labelLayout}
              {...hoverHandlers}
            >
              {label}
            </Box>
          ) : (
            <Box
              key={segment.id}
              style={style}
              className={SEGMENT_MOTION}
              {...labelLayout}
              {...hoverHandlers}
            >
              {label}
            </Box>
          );
        })}
      </Box>
      {hover && renderTooltip ? (
        <ChartTooltipCard left={hover.left} top={hover.top}>
          {renderTooltip(hover.datum)}
        </ChartTooltipCard>
      ) : null}
    </Box>
  );
}
